import { Box, CircularProgress, Stack } from "@mui/material";
import React, { useState } from "react";

import { useCamera } from "hooks/useCamera/useCamera";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { Typography } from "new-components/typography/Typography";

import exploreUseExampleStepsSrc from "../assets/demo-usage-step-by-step-example.png";
import takeAPictureSrc from "../assets/take-a-picture.png";
import { DEFAULT_DETECTOR_QUERY_EXAMPLE } from "../constants";

import { ThirdStep } from "./DemoPageOnboardingThirdStep";

type GetCustomConfirmationParams = {
  isCameraEnabled: boolean;
  nextStep: VoidFunction;
  queryText: string;
  onSubmit: (queryText: string) => void;
  hasRequestedCameraAccess: boolean;
  setHasRequestedCameraAccess: (hasRequestedCameraAccess: boolean) => void;
  requestCameraAccess: () => void;
};

type CustomConfirmationProps = {
  label: string;
  onClick: VoidFunction;
  disabled?: boolean;
};

type GetErrorParams = {
  isCameraEnabled: boolean;
  hasRequestedCameraAccess: boolean;
};

type BodyRendererProps = {
  queryText: string;
  setQueryText: (queryText: string) => void;
  setIsInvalid: (isInvalid: boolean) => void;
};

type Step = {
  headline: string;
  renderBody: (props: BodyRendererProps) => React.JSX.Element;
  description: string;
  getCustomConfirmationProps?: (params: GetCustomConfirmationParams) => CustomConfirmationProps;
  getError?: (params: GetErrorParams) => string | null;
};

const STEPS: Step[] = [
  {
    headline:
      "Demo lets you do quick experiments with a webcam to learn about how to create and use a Groundlight detector",
    renderBody: () => <Box component="img" src={takeAPictureSrc} className="h-[250px]" />,
    description:
      "Ask a question, capture images, and get answers as your demo detector creates a custom machine learning model with human oversight.",
  },
  {
    headline:
      "Use the ‘capture image’ button to submit a few image queries that you think will return ‘yes’ and ‘no’ answers. ",
    renderBody: () => (
      <Box className="mx-auto">
        <Box component="img" src={exploreUseExampleStepsSrc} className="h-[125px]" />
      </Box>
    ),
    description:
      "Initial image queries will have low-confidence ML predictions and then get answered by cloud labelers. Over time, the model improves and your image queries will get higher confidence answers directly from the ML model. ",
    getCustomConfirmationProps: ({ nextStep, isCameraEnabled, hasRequestedCameraAccess, requestCameraAccess }) => {
      if (isCameraEnabled) {
        return {
          onClick: nextStep,
          label: "Next",
        };
      }

      return {
        onClick: requestCameraAccess,
        label: "Enable webcam",
        disabled: !isCameraEnabled && hasRequestedCameraAccess,
      };
    },
    getError: ({ isCameraEnabled, hasRequestedCameraAccess }) => {
      if (!isCameraEnabled && hasRequestedCameraAccess) {
        return "Note: This tool requires camera access to function properly. Please enable camera permissions to continue.";
      }

      return null;
    },
  },
  {
    headline: "Ask a yes/no question about the webcam image.",
    renderBody: ThirdStep,
    description:
      "Start out with something simple that you can easily capture images of, such as “Is the person holding a pen?” or “Does the person have glasses on?” ",
    getCustomConfirmationProps: ({ queryText, onSubmit }) => ({
      label: "Let's go!",
      onClick: () => {
        onSubmit(queryText);
      },
      disabled: queryText === "",
    }),
  },
];

type FirstUseOfExplorestepperProps = {
  initialStep: number | null;
  onSubmit: (labelPrompt: string) => void;
  loading: boolean;
};

export function DemoPageOnboarding({ initialStep, onSubmit, loading }: FirstUseOfExplorestepperProps) {
  const [step, setStep] = useState(initialStep ?? 0);
  const stepOrdinal = step + 1;
  const camera = useCamera();
  const [hasRequestedCameraAccess, setHasRequestedCameraAccess] = useState(false);

  const resetCameraChecks = () => {
    setHasRequestedCameraAccess(false);
    camera.checkAccess();
  };

  const nextStep = () => {
    resetCameraChecks();
    setStep(step + 1);
  };

  const previousStep = () => {
    resetCameraChecks();
    setStep(step - 1);
  };

  const handleCameraRequestAccess = async () => {
    try {
      await camera.requestAccess();
    } finally {
      setHasRequestedCameraAccess(true);
    }
  };

  const [queryText, setQueryText] = useState(DEFAULT_DETECTOR_QUERY_EXAMPLE);
  const [isInvalid, setIsInvalid] = useState(false);

  const stepContent = STEPS[step];
  const { renderBody: StepBody } = stepContent;
  const customConfirmationProps = stepContent.getCustomConfirmationProps?.({
    nextStep,
    isCameraEnabled: camera.hasAccess,
    queryText,
    onSubmit,
    hasRequestedCameraAccess,
    setHasRequestedCameraAccess,
    requestCameraAccess: handleCameraRequestAccess,
  });

  const handleNext = () => {
    setIsInvalid(false);

    const handler = customConfirmationProps?.onClick ?? nextStep;
    handler();
  };

  const handlePrevious = () => {
    setIsInvalid(false);
    previousStep();
  };

  return (
    <Box className="max-w-[908px] w-full mx-auto p-5" minHeight="calc(100vh - 150px)" alignContent="center">
      <Stack className="h-[510px]" spacing="30px" alignItems="center" justifyContent="center">
        <Typography
          variant="title"
          fontWeight="bold"
          className="text-primary-bright w-full text-left"
        >
          {stepContent.headline}
        </Typography>
        <Stack className="w-full h-full" spacing="20px">
          <Box className="flex items-center justify-center w-full mb-2">
            <StepBody queryText={queryText} setQueryText={setQueryText} setIsInvalid={setIsInvalid} />
          </Box>
          <Typography
            variant="subtitle"
            className="text-neutral-darkest text-xl"
          >
            {stepContent.description}
          </Typography>
        </Stack>
        <Typography
          variant="subtitle"
          fontWeight="bold"
          className="text-red"
        >
          {stepContent.getError?.({
            isCameraEnabled: camera.hasAccess,
            hasRequestedCameraAccess
          })}
        </Typography>
        <Stack direction="row" justifyContent="center" alignItems="center" className="w-full relative">
          <Typography
            variant="subtitle"
            className="text-neutral-medium absolute left-0"
          >
            Step: {stepOrdinal} / {STEPS.length}
          </Typography>
          <Stack direction="row" spacing="15px">
            <GroundlightButton
              variant="plain"
              onClick={handlePrevious}
              disabled={step === 0}
              sx={{ width: "200px" }}>
              Back
            </GroundlightButton>
            <GroundlightButton
              variant="filled"
              onClick={handleNext}
              disabled={customConfirmationProps?.disabled || isInvalid}
              sx={{ width: "200px" }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                customConfirmationProps?.label ?? "Next"
              )}
            </GroundlightButton>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
